import './ConversionLatencyHistogram.scss';
import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import { formatPercent } from '@blisspointmedia/bpm-types/dist/formatUtils';
import {
  GetHistogramParams,
  HistogramRow,
  PerformancePreset as Preset,
  Prefix as StreamingPrefix
} from '@blisspointmedia/bpm-types/dist/StreamingPerformance';
import { ChartContainer } from '@Components/ChartContainer';
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH
} from '@Components/Charts/ChartConstants';
import { ChartTooltipV2 } from '@Components/Charts/ChartTooltip/ChartTooltipV2';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Dropdown, DropdownToggleType } from '@Components/Dropdown';
import { Size } from '@Components/designConstants';
import { AxesSkeleton, BarGraphSkeleton, Skeleton } from '@Components/Skeleton';
import { useSetError } from '@src/redux/modals';
import { Brand60 } from '@src/utils/colors';
import { downloadPNG, exportToExcel } from '@src/utils/download-utils';
import { awaitJSON, StreamingPerformanceLambdaFetch } from '@src/utils/fetch-utils';
import { VIEW_TYPE_OPTIONS } from './conversion-latency-histogram-constants';

interface ConversionLatencyHistogramProps {
  company: string;
  kpi: string;
  start: string;
  end: string;
  prefix: StreamingPrefix;
  preset?: Preset;
  isGraph?: boolean;
}

export const ConversionLatencyHistogram = ({
  company,
  kpi,
  start,
  end,
  prefix,
  preset,
  isGraph
}: ConversionLatencyHistogramProps) => {
  const setError = useSetError();
  const [viewType, setViewType] = useState('Discrete');

  const impsBetween = preset?.impsBetween || true;
  const { data: histData, error } = useQuery({
    queryKey: ['histogram', company, kpi, start, end, prefix, impsBetween, isGraph],
    queryFn: async () => {
      const res = await StreamingPerformanceLambdaFetch<GetHistogramParams>('/histogram', {
        params: { company, kpi, start, end, prefix, impsBetween, isGraph }
      });
      return awaitJSON<HistogramRow[]>(res);
    },
    meta: { suppressGlobalError: true }
  });

  useEffect(() => {
    if (error) {
      setError({ message: error.message, reportError: error });
    }
  }, [error, setError]);

  const excelDownload = useCallback(() => {
    exportToExcel(histData || [], 'Streaming Conversion Latency');
  }, [histData]);

  const pngDownload = useCallback(async () => {
    await downloadPNG('.conversion-latency-container', 'Streaming Conversion Latency');
  }, []);

  return (
    <ChartContainer
      title={
        <>
          <Dropdown
            type={DropdownToggleType.WIDGET_TITLE}
            value={viewType}
            options={VIEW_TYPE_OPTIONS}
            onChange={setViewType}
          />
          <div>Conversion Latency</div>
        </>
      }
      rightActions={
        <DownloadDropdown
          size={Size.MD}
          onClickOptions={[excelDownload, pngDownload]}
          disabled={!histData}
        />
      }
    >
      <div className='conversion-latency-container conversion-latency-chart'>
        <HistogramBody histData={histData} viewType={viewType} />
      </div>
    </ChartContainer>
  );
};

interface HistogramBodyProps {
  histData: HistogramRow[] | undefined;
  viewType: string;
}

const HistogramBody = ({ histData, viewType }: HistogramBodyProps) => {
  if (!histData) {
    return (
      <Skeleton>
        <BarGraphSkeleton barWidth={14} gutter={3} verticalPadding={5} />
        <AxesSkeleton size={3} />
      </Skeleton>
    );
  }
  if (!histData.length) {
    return <div className='no-data'>No conversion latency data.</div>;
  }
  return (
    <AutoSizer
      renderProp={({ width, height }) => (
        <BarChart
          data={histData}
          width={width}
          height={height}
          margin={{ top: 10, right: 20, bottom: 10, left: 10 }}
        >
          <CartesianGrid
            vertical={false}
            stroke={CARTESIAN_GRID_STROKE}
            strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
          />
          <Bar dataKey={viewType === 'Discrete' ? 'pct' : 'cumulative'} fill={Brand60} />
          <XAxis dataKey='day' tickFormatter={day => `Day ${day}`} tick={{ fontSize: 12 }} />
          <YAxis
            width={30}
            dataKey={viewType === 'Discrete' ? 'pct' : 'cumulative'}
            tickFormatter={val => formatPercent(val as number, 0)}
            interval={0}
            tick={{ fontSize: 12 }}
          />
          <Tooltip content={<HistogramTooltipContent />} cursor={{ opacity: 0.4 }} />
        </BarChart>
      )}
    />
  );
};

interface HistogramTooltipContentProps {
  label?: string | number;
  payload?: readonly { name?: string; value?: number }[];
}

const HistogramTooltipContent = ({ label, payload }: HistogramTooltipContentProps) => {
  const items =
    payload?.map(item => {
      const name = item.name ?? '';
      const value = item.value ?? 0;
      return {
        label: name === 'pct' ? 'Percent' : 'Cumulative',
        value: formatPercent(value, 2),
        color: Brand60
      };
    }) || [];

  const formattedLabel = `${label} day${label === 1 ? '' : 's'} later`;
  return <ChartTooltipV2 headers={[formattedLabel]} items={items} itemKeyShape='square' />;
};
